import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { borrarAdjunto, type Adjunto, PropositoAdjunto } from "../api";
import { formatearBytes, formatearFecha } from "../formato";

type VisorProps = {
  adjunto: Adjunto | null;
  onCerrar: () => void;
  puedeEditar?: boolean;
};

const ETIQUETAS_PROPOSITO: Record<number, string> = {
  [PropositoAdjunto.FotoPerfil]: "Foto de perfil",
  [PropositoAdjunto.Carnet]: "Carnet de vacunas",
  [PropositoAdjunto.Receta]: "Receta médica",
  [PropositoAdjunto.Examen]: "Examen médico",
  [PropositoAdjunto.Otro]: "Documento / Foto",
};

export default function VisorAdjunto({ adjunto, onCerrar, puedeEditar = false }: VisorProps) {
  const qc = useQueryClient();
  const [confirmandoBorrar, setConfirmandoBorrar] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCerrar();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onCerrar]);

  const mutacionBorrar = useMutation({
    mutationFn: (adjuntoID: string) => borrarAdjunto(adjuntoID),
    onSuccess: () => {
      if (adjunto) {
        qc.invalidateQueries({ queryKey: ["adjuntos", adjunto.mascotaID] });
        qc.invalidateQueries({ queryKey: ["mascota", adjunto.mascotaID] });
        qc.invalidateQueries({ queryKey: ["visitas", adjunto.mascotaID] });
        qc.invalidateQueries({ queryKey: ["mascotas"] });
      }
      onCerrar();
    },
  });

  if (!adjunto) return null;

  const esImagen = adjunto.contentType.startsWith("image/");
  const esPdf = adjunto.contentType === "application/pdf";
  const etiqueta = ETIQUETAS_PROPOSITO[adjunto.proposito] ?? "Adjunto";

  return (
    <div className="modal-backdrop" onClick={onCerrar}>
      <div className="visor-dialog panel" onClick={(e) => e.stopPropagation()}>
        <header className="visor-cabecera">
          <div>
            <span className="etiqueta neutra">{etiqueta}</span>
            <span className="muted" style={{ marginLeft: "0.5rem", fontSize: "0.85rem" }}>
              Subido el {formatearFecha(adjunto.creadoUtc)} · {formatearBytes(adjunto.bytes)}
            </span>
          </div>

          <button
            type="button"
            className="boton-chico"
            onClick={onCerrar}
            title="Cerrar visor"
          >
            ✕
          </button>
        </header>

        <div className="visor-cuerpo">
          {esImagen && (
            <div className="visor-img-wrap">
              <img
                src={adjunto.url}
                alt={etiqueta}
                className="visor-img"
              />
            </div>
          )}

          {esPdf && (
            <div className="visor-pdf-wrap">
              <div className="visor-pdf-info">
                <span style={{ fontSize: "3rem" }}>📄</span>
                <p><strong>Documento PDF</strong></p>
                <p className="muted">{formatearBytes(adjunto.bytes)}</p>
                <a
                  href={adjunto.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="boton-primario"
                  style={{ marginTop: "1rem" }}
                >
                  Abrir documento en pestaña nueva ↗
                </a>
              </div>
            </div>
          )}

          {!esImagen && !esPdf && (
            <div className="visor-archivo-wrap">
              <span style={{ fontSize: "3rem" }}>📁</span>
              <p><strong>Archivo adjunto</strong></p>
              <p className="muted">{adjunto.contentType} · {formatearBytes(adjunto.bytes)}</p>
              <a
                href={adjunto.url}
                target="_blank"
                rel="noopener noreferrer"
                className="boton-primario"
              >
                Descargar archivo ↗
              </a>
            </div>
          )}
        </div>

        <footer className="visor-acciones">
          <div className="acciones-izq">
            <a
              href={adjunto.url}
              target="_blank"
              rel="noopener noreferrer"
              className="boton boton-chico"
            >
              Abrir original ↗
            </a>
          </div>

          {puedeEditar && (
            <div className="acciones-der">
              {confirmandoBorrar ? (
                <div className="confirmar-borrar-inline">
                  <button
                    type="button"
                    className="boton-peligro boton-chico"
                    disabled={mutacionBorrar.isPending}
                    onClick={() => mutacionBorrar.mutate(adjunto.adjuntoID)}
                  >
                    Confirmar eliminación
                  </button>
                  <button
                    type="button"
                    className="boton-chico"
                    onClick={() => setConfirmandoBorrar(false)}
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="boton-peligro boton-chico"
                  onClick={() => setConfirmandoBorrar(true)}
                >
                  Eliminar archivo
                </button>
              )}
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}
